import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { PieChartComponent } from "@/types/insights";
import { useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";
import { ChartCard } from "./InsightsComponentCard";
import { buildChartConfig, CHART_COLORS, formatNumber } from "./insights-component-utils";

type PieSliceLabelProps = {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  midAngle?: number;
  value?: number | string;
};

const RADIAN = Math.PI / 180;

const renderPieSliceLabel = ({
  cx,
  cy,
  innerRadius = 0,
  outerRadius = 0,
  midAngle = 0,
  value,
}: PieSliceLabelProps) => {
  if (typeof cx !== "number" || typeof cy !== "number" || typeof value !== "number") {
    return null;
  }

  const radius = Math.max(innerRadius, outerRadius) + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="currentColor"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-semibold"
    >
      {formatNumber(value)}
    </text>
  );
};

export const InsightsPieChartCard = ({ component }: { component: PieChartComponent }) => {
  const data = useMemo(
    () =>
      component.data.map((slice, index) => ({
        ...slice,
        fill: CHART_COLORS[index % CHART_COLORS.length],
      })),
    [component.data]
  );

  const config = useMemo(
    () => buildChartConfig(component.data.map((slice) => ({ key: slice.label, label: slice.label }))),
    [component]
  );

  if (data.length === 0) return null;

  return (
    <ChartCard title={component.title} description={component.description}>
      <ChartContainer config={config} className="h-72 w-full aspect-auto">
        <PieChart>
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) => (
                  <span className="font-mono tabular-nums">
                    {typeof value === "number" ? formatNumber(value) : String(value)}
                  </span>
                )}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent nameKey="label" />} />
          <Pie data={data} dataKey="value" nameKey="label" label={renderPieSliceLabel} labelLine={false}>
            {component.totalValue ? (
              <Label
                content={({ viewBox }) => {
                  if (!viewBox || !("cx" in viewBox) || !("cy" in viewBox)) return null;

                  return (
                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                      <tspan className="fill-white text-base font-semibold" x={viewBox.cx} dy="-0.2em">
                        {component.totalValue}
                      </tspan>
                      {component.totalLabel ? (
                        <tspan className="fill-white/80 text-xs" x={viewBox.cx} dy="1.6em">
                          {component.totalLabel}
                        </tspan>
                      ) : null}
                    </text>
                  );
                }}
              />
            ) : null}
          </Pie>
        </PieChart>
      </ChartContainer>
    </ChartCard>
  );
};
